import ExcelJS from "exceljs";
import {
	openCostingFabricAssocObjKey,
	openCostingPackingTrimsAssocObjKey,
	openCostingValueAddAssocObjKey,
	openCostingOtherChargesAssocObjkey,
	openCostingPoCostAssocObjkey,
	openCostingSewingTrimsAssocObjKey,
	openCostingTrimsDynamicConsumpAssocObjKey,
} from "../CreateOpenCosting/data";
import { Buffer } from "buffer";
import {
	openCostingChannelWhSplitAssockey,
	openCostingQtySizeSplitAssockey,
} from "../SelectedOpenCosting/Departments/CostingDetails/data";

export const lsFilterkeyStr = "open_costing_filter_obj";

const columns = {
	fabric_details: { name: "Fabric Details", key: "fabric_details", assoc: openCostingFabricAssocObjKey },
	sweing_trims_details: { name: "Sewing Trims Details", key: "sweing_trims_details", assoc: openCostingSewingTrimsAssocObjKey },
	packing_trims_details: { name: "Packing Trims", key: "packing_trims_details", assoc: openCostingPackingTrimsAssocObjKey },
	carton_tape_details: {
		name: "Carton & Tape (Dynamic Consumption)",
		key: "carton_tape_details",
		assoc: openCostingTrimsDynamicConsumpAssocObjKey,
	},
	value_addition_details: { name: "Value Addition", key: "value_addition_details", assoc: openCostingValueAddAssocObjKey },
	garment_total_details: { name: "Garment Total", key: "garment_total_details", assoc: "" },
	final_total_details: { name: "Final Total", key: "final_total_details", assoc: openCostingOtherChargesAssocObjkey },
	po_cost_details: { name: "PO Cost", key: "po_cost_details", assoc: openCostingPoCostAssocObjkey },
};

const wh_columns = {
	qty_size_split: { name: "Qty Size Split", key: "qty_size_split", assoc: openCostingQtySizeSplitAssockey },
	qty_wh_split: { name: "Channel Warehouse Split", key: "qty_wh_split", assoc: openCostingChannelWhSplitAssockey },
};

export const generateCostingDetailsExcel = async (
	costData,
	exclude_list = ["last_updated_by", "published_by", "created_by", "published_date", "createdAt", "updatedAt", "status"]
) => {
	const workbook = new ExcelJS.Workbook();
	const sheet = workbook.addWorksheet("Costing");

	let currentRow = 1;
	let garmentTotal = 0;
	let finalTotalValue = 0;
	let poCostTotalValue = null;

	const formatHeaderLabel = (key) =>
		key.replace(/[_-]+/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

	// --- Basic Info Section ---
	for (const [key, value] of Object.entries(costData)) {
		if (key === "url" || exclude_list.includes(key)) continue;
		if (Array.isArray(value)) continue;

		const keyCell = sheet.getCell(`A${currentRow}`);
		keyCell.value = formatHeaderLabel(key);
		keyCell.font = { bold: true };

		const valueCell = sheet.getCell(`B${currentRow}`);
		valueCell.value = value;
		valueCell.alignment = { horizontal: "center" };

		currentRow++;
	}

	// --- Add Image ---
	try {
		const response = await fetch(costData.url, { cache: "no-store", mode: "cors" });
		if (!response.ok) throw new Error(`Image fetch failed: ${response.status}`);
		const blob = await response.blob();
		const arrayBuffer = await blob.arrayBuffer();
		const buffer = Buffer.from(arrayBuffer);
		const extMatch = costData.url.match(/\.(jpg|jpeg|png|gif)$/i);
		const extension = extMatch ? extMatch[1].toLowerCase() : "jpeg";
		const imageId = workbook.addImage({ buffer, extension });

		sheet.addImage(imageId, {
			tl: { col: 2.5, row: 1 },
			ext: { width: 190, height: 240 },
		});
	} catch (err) {
		console.error("Image embedding failed:", err);
	}

	// --- Loop through sections ---
	for (const colKey in columns) {
		const { name: title, assoc: association } = columns[colKey];
		let value = costData[association];

		// Skip PO Cost if discounting disabled
		if (
			association === columns["po_cost_details"].assoc &&
			["no", "false", false].includes(String(costData.discounting_applied).toLowerCase())
		) {
			continue;
		}

		if (!Array.isArray(value) || value.length === 0 || typeof value[0] !== "object") continue;

		currentRow += 2;

		const rawKeys = Object.keys(value[0]).filter((h) => !exclude_list.includes(h) && h !== "style_ref_no");
		const endKeys = ["consumption", "price", "value"];
		const orderedKeys = [
			"id",
			"supplier",
			...rawKeys.filter((k) => k !== "id" && k !== "supplier" && !endKeys.includes(k)),
			...endKeys.filter((k) => rawKeys.includes(k)),
		];

		// Title row
		sheet.mergeCells(currentRow, 1, currentRow, orderedKeys.length);
		const titleCell = sheet.getCell(currentRow, 1);
		titleCell.value = title;
		titleCell.font = { bold: true, size: 13 };
		titleCell.alignment = { horizontal: "center" };
		titleCell.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "FFFAF3D1" },
		};
		currentRow++;

		// Header row
		orderedKeys.forEach((header, i) => {
			const cell = sheet.getCell(currentRow, i + 1);
			cell.value = formatHeaderLabel(header);
			cell.font = { bold: true };
			cell.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FFD3D3D3" },
			};
			cell.alignment = { horizontal: "center" };
		});
		currentRow++;

		// --- Final Total Section ---
		if (association === columns["final_total_details"].assoc) {
			const categoryIndex = orderedKeys.indexOf("category") + 1;
			const valueIndex = orderedKeys.indexOf("value") + 1;

			const garmentRow = sheet.getRow(currentRow);
			garmentRow.getCell(categoryIndex).value = "Garment Total";
			garmentRow.getCell(categoryIndex).font = { bold: true };
			garmentRow.getCell(categoryIndex).alignment = { horizontal: "center" };
			garmentRow.getCell(valueIndex).value = garmentTotal;
			garmentRow.getCell(valueIndex).font = { bold: true };
			garmentRow.getCell(valueIndex).alignment = { horizontal: "center" };
			currentRow++;

			let finalTotal = garmentTotal;

			value.forEach((item) => {
				orderedKeys.forEach((header, i) => {
					const cell = sheet.getCell(currentRow, i + 1);
					cell.value = item[header];
					cell.alignment = { horizontal: "center" };
				});
				finalTotal += typeof item.value === "number" ? item.value : 0;
				currentRow++;
			});

			// Add TOTAL row inside Final Total section
			const totalRow = currentRow;
			const labelSpan = orderedKeys.findIndex((k) => ["price", "value"].includes(k));
			if (labelSpan > 0) {
				sheet.mergeCells(totalRow, 1, totalRow, labelSpan);
				const labelCell = sheet.getCell(totalRow, 1);
				labelCell.value = "FINAL TOTAL";
				labelCell.font = { bold: true };
				labelCell.alignment = { horizontal: "center" };
				labelCell.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "FFF0F0F0" },
				};
			}

			orderedKeys.forEach((key, i) => {
				const col = i + 1;
				const cell = sheet.getCell(totalRow, col);
				if (key === "value") {
					cell.value = finalTotal;
					cell.font = { bold: true };
					cell.alignment = { horizontal: "center" };
				}
			});

			finalTotalValue = finalTotal;
			currentRow += 2;
			continue;
		}

		// --- PO Cost Section (no total row inside) ---
		if (colKey === "po_cost_details") {
			value.forEach((item) => {
				orderedKeys.forEach((header, i) => {
					const cell = sheet.getCell(currentRow, i + 1);
					cell.value = item[header];
					cell.alignment = { horizontal: "center" };
				});
				currentRow++;
			});
			poCostTotalValue = value.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
			currentRow += 2;
			continue;
		}

		// --- Regular Section ---
		value.forEach((item) => {
			orderedKeys.forEach((header, i) => {
				const cell = sheet.getCell(currentRow, i + 1);
				cell.value = item[header];
				cell.alignment = { horizontal: "center" };
			});
			currentRow++;
		});

		// Add TOTAL row for regular sections
		const totalRow = currentRow;
		let priceSum = 0;
		let valueSum = 0;

		value.forEach((item) => {
			priceSum += typeof item.price === "number" ? item.price : 0;
			valueSum += typeof item.value === "number" ? item.value : 0;
		});

		if (colKey !== "final_total_details" && colKey !== "po_cost_details") {
			garmentTotal += valueSum;
		}

		const labelSpan = orderedKeys.findIndex((k) => ["price", "value"].includes(k));
		if (labelSpan > 0) {
			sheet.mergeCells(totalRow, 1, totalRow, labelSpan);
			const labelCell = sheet.getCell(totalRow, 1);
			labelCell.value = "TOTAL";
			labelCell.font = { bold: true };
			labelCell.alignment = { horizontal: "center" };
			labelCell.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FFF0F0F0" },
			};
		}

		orderedKeys.forEach((key, i) => {
			const col = i + 1;
			const cell = sheet.getCell(totalRow, col);
			if (key === "price") {
				cell.value = priceSum;
				cell.font = { bold: true };
				cell.alignment = { horizontal: "center" };
			}
			if (key === "value") {
				cell.value = valueSum;
				cell.font = { bold: true };
				cell.alignment = { horizontal: "center" };
			}
		});

		currentRow += 2;
	}

	// --- Final Summary Row ---
	currentRow += 2;
	const summaryRow = sheet.getRow(currentRow);

	summaryRow.getCell(1).value = "FINAL TOTAL";
	summaryRow.getCell(1).font = { bold: true };
	summaryRow.getCell(1).alignment = { horizontal: "center" };
	summaryRow.getCell(1).fill = {
		// <-- add this
		type: "pattern",
		pattern: "solid",
		fgColor: { argb: "FFF0F0F0" }, // grey color
	};

	summaryRow.getCell(2).value = finalTotalValue || 0;
	summaryRow.getCell(2).font = { bold: true };
	summaryRow.getCell(2).alignment = { horizontal: "center" };

	summaryRow.getCell(3).value = "PO COST";
	summaryRow.getCell(3).font = { bold: true };
	summaryRow.getCell(3).alignment = { horizontal: "center" };
	summaryRow.getCell(3).fill = {
		// <-- add this
		type: "pattern",
		pattern: "solid",
		fgColor: { argb: "FFF0F0F0" }, // grey color
	};

	const poValue = poCostTotalValue !== null ? poCostTotalValue : finalTotalValue;
	summaryRow.getCell(4).value = poValue || 0;
	summaryRow.getCell(4).font = { bold: true };
	summaryRow.getCell(4).alignment = { horizontal: "center" };

	// --- Column sizing ---
	sheet.getColumn(1).width = 20;
	sheet.getColumn(2).width = 30;
	sheet.getColumn(3).width = 40;

	// --- Warehouse Splits Sheet ---
	const whSheet = workbook.addWorksheet("Warehouse Splits");
	let whRow = 1;

	for (const colKey in wh_columns) {
		const { name: title, assoc: association } = wh_columns[colKey];
		const value = costData[association];
		if (!Array.isArray(value) || value.length === 0 || typeof value[0] !== "object") continue;

		whRow += 2;
		const rawKeys = Object.keys(value[0]);
		const orderedKeys = ["id", ...rawKeys.filter((k) => k !== "id")];

		// Title
		whSheet.mergeCells(whRow, 1, whRow, orderedKeys.length);
		const titleCell = whSheet.getCell(whRow, 1);
		titleCell.value = title;
		titleCell.font = { bold: true, size: 13 };
		titleCell.alignment = { horizontal: "center" };
		titleCell.fill = {
			type: "pattern",
			pattern: "solid",
			fgColor: { argb: "FFFAF3D1" },
		};
		whRow++;

		// Header
		orderedKeys.forEach((header, i) => {
			const cell = whSheet.getCell(whRow, i + 1);
			cell.value = formatHeaderLabel(header);
			cell.font = { bold: true };
			cell.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FFD3D3D3" },
			};
			cell.alignment = { horizontal: "center" };
		});
		whRow++;

		// Data
		value.forEach((item) => {
			orderedKeys.forEach((key, i) => {
				const cell = whSheet.getCell(whRow, i + 1);
				cell.value = item[key];
				cell.alignment = { horizontal: "center" };
			});
			whRow++;
		});
	}

	whSheet.getColumn(1).width = 20;
	whSheet.getColumn(2).width = 30;
	whSheet.getColumn(3).width = 40;

	// --- Download ---
	const bufferOut = await workbook.xlsx.writeBuffer();
	const blobOut = new Blob([bufferOut], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const link = document.createElement("a");
	link.href = URL.createObjectURL(blobOut);
	link.download = `${costData.style_ref_no || "costing"}_details.xlsx`;
	link.click();

	return true;
};
